import { StoreDoc, UserDoc } from '@gooduncles/gu-app-schema';
import { Button, message } from 'antd';
import { FC, useMemo, useState } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { RangeEventValue, dayjsesToStringDateWhere, getDefaultDates } from 'src/utils/date-util';
import { onDownloadOrderHistoryExcel } from 'src/utils/order-util';

import TableOptions from 'src/components/Common/TableOptions/TableOptions';
import ChangePasswordButton from 'src/components/User/UserTable/ChangePasswordButton/ChangePasswordButton';
import LinkPhoneButton from 'src/components/User/UserTable/LinkPhoneButton/LinkPhoneButton';
import RevokeBiometricLinksButton from 'src/components/User/UserTable/RevokeBiometricLinksButton/RevokeBiometricLinksButton';

import classes from './UserOption.module.scss';

type UserOptionProps = {
  userWithStore: UserDoc & {
    store: StoreDoc | null;
  };
};

const defaultValue = getDefaultDates();

const UserOption: FC<UserOptionProps> = ({ userWithStore }) => {
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState<RangeEventValue>(null);
  const [leftDate, rightDate] = useMemo(() => {
    return dates?.[0] && dates?.[1]
      ? dayjsesToStringDateWhere(dates[0], dates[1])
      : dayjsesToStringDateWhere(defaultValue[0], defaultValue[1]);
  }, [dates]);

  const downloadOrderHistory = async () => {
    setLoading(true);
    try {
      if (!userWithStore.store) {
        throw new Error('가게 정보가 없습니다.');
      }
      onDownloadOrderHistoryExcel(userWithStore.store, leftDate, rightDate);
    } catch (error) {
      console.error(error);
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage);
    }
    setLoading(false);
  };

  const userLabel = userWithStore.email ?? userWithStore.userName ?? userWithStore._id;

  return (
    <div className={classes.userOptionContainer}>
      <div>
        <h3>식당 주문 내역 엑셀 다운로드</h3>
        <p style={{ marginTop: 8 }}>
          식당의 주문 내역과 이슈를 상품단위로 잘라서 엑셀로 다운로드합니다.
          <br />
          기간을 선택하고 다운로드 버튼을 눌러주세요.
        </p>
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
          <TableOptions setDates={setDates} defaultValue={defaultValue} marginBottom={0} />
          <Button loading={loading} onClick={downloadOrderHistory} style={{ marginLeft: 8 }}>
            다운로드
          </Button>
        </div>
      </div>
      <div>
        <h3>전화번호 연동 관리(관리자)</h3>
        <p style={{ marginTop: 8 }}>
          SMS 인증 없이 강제로 전화번호를 연동/해제합니다.
          <br />
          본인 확인 절차를 거친 뒤에만 사용하세요.
        </p>
        <div style={{ marginTop: 16 }}>
          <LinkPhoneButton
            userId={userWithStore._id}
            defaultPhone={userWithStore.userTel ?? undefined}
            userLabel={userLabel}
            phoneLinked={userWithStore.phoneLinked}
          />
        </div>
      </div>
      <div>
        <h3>비밀번호 변경(관리자)</h3>
        <p style={{ marginTop: 8 }}>
          Firebase Auth 비밀번호를 직접 교체합니다. 되돌릴 수 없으며 매장 앱은 자동 로그아웃됩니다.
          <br />
          v2 안정화 이후 삭제될 임시 기능입니다.
        </p>
        <div style={{ marginTop: 16 }}>
          <ChangePasswordButton
            userId={userWithStore._id}
            userLabel={userLabel}
            targetRole={userWithStore.role ?? null}
            storeName={userWithStore.store?.storeNickname ?? userWithStore.store?.storeName ?? null}
            storeCode={userWithStore.store?.storeCode ?? null}
          />
        </div>
      </div>
      <div>
        <h3>생체인증 연동 회수(관리자)</h3>
        <p style={{ marginTop: 8 }}>
          단말 분실·탈취 시 사용자의 모든 생체인증(지문/Face ID) 기기 연동을 회수합니다.
          <br />
          되돌릴 수 없으며, 본인 확인 절차를 거친 뒤에만 사용하세요.
        </p>
        <div style={{ marginTop: 16 }}>
          <RevokeBiometricLinksButton
            userId={userWithStore._id}
            userLabel={userLabel}
            storeName={userWithStore.store?.storeNickname ?? userWithStore.store?.storeName ?? null}
            storeCode={userWithStore.store?.storeCode ?? null}
          />
        </div>
      </div>
    </div>
  );
};

export default UserOption;
